import { useState, useEffect } from "react"
import { api } from "aldoo-ra/API"
import { url } from "aldoo-ra/CMS/url"
import Typography from "aldoo-ra/Typography"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import TextBundle from "aldoo-ra/CMS/text-bundle"

const hcAPI = api("help-center")

const FeaturedCategories = () => {
  const texts = TextBundle({ id: "help-center" })
  const [items, setItems] = useState([])
  const [sectionName, setSectionName] = useState("Featured")
  const [isLoading, setIsLoading] = useState(true)

  const [currentLanguage] = useState(() => {
    try {
      const stored = localStorage.getItem("current-site-language")
      return stored ? stored.replace(/"/g, "") : "en"
    } catch { return "en" }
  })

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await hcAPI({
          get_content: { contentURL: "/category/featured", language: currentLanguage },
        })

        const content = response?.content
        if (!content) { setItems([]); return }

        setSectionName(content.name || "Featured")

        // Categories — fetch each one's articles
        const publishedCategoryRefs = (content.categories || []).filter(
          (c) => c.status === "published"
        )

        const categoryItems = await Promise.all(
          publishedCategoryRefs.map(async (catRef) => {
            try {
              const catData = await hcAPI({
                get_content: { contentURL: `/category/${catRef.slug}`, language: currentLanguage },
              })
              const subcategories = (catData?.content?.categories || [])
                .filter((c) => c.status === "published")
                .map((c) => ({ ...c, _itemType: "category" }))
              const articles = (catData?.content?.articles || [])
                .filter((a) => a.status === "published")
                .map((a) => ({ ...a, _itemType: "article" }))
              return {
                _id: catRef._id,
                _type: "category",
                label: catRef.label || catData?.content?.name,
                slug: catRef.slug,
                icon: catRef.icon || catData?.content?.icon,
                status: catRef.status,
                children: [...subcategories, ...articles],
              }
            } catch {
              return { ...catRef, _type: "category", children: [] }
            }
          })
        )

        // Direct articles
        const articleItems = (content.articles || [])
          .filter((a) => a.status === "published")
          .map((a) => ({ ...a, _type: "article" }))

        setItems([
          ...categoryItems.filter((c) => c.status === "published"),
          ...articleItems,
        ])
      } catch {
        setItems([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchItems()
  }, [currentLanguage])

  const handleCategoryClick = (slug) => {
    if (window.loadHelpCenterContent) {
      window.loadHelpCenterContent("category", slug, null)
    } else {
      window.location.href = `/help-center/category/${slug}`
    }
  }

  const handleArticleClick = (e, categorySlug, articleSlug) => {
    e.preventDefault()
    if (window.loadHelpCenterContent) {
      window.loadHelpCenterContent("article", articleSlug, categorySlug)
    } else {
      const path = categorySlug
        ? `/help-center/category/${categorySlug}/article/${articleSlug}`
        : `/help-center/article/${articleSlug}`
      window.location.href = path
    }
  }

  const handleDirectArticleClick = (slug) => {
    if (window.loadHelpCenterContent) {
      window.loadHelpCenterContent("article", slug, null)
    } else {
      window.location.href = `/help-center/article/${slug}`
    }
  }

  if (isLoading) return (
    <div className="py-8 text-center">
      <Typography variant="txt1_dim">
        {texts?.loading_featured_cat || "Loading featured categories..."}
      </Typography>
    </div>
  )

  if (!items.length) return null

  return (
    <div className="mt-14">
      <Typography variant="h2" className="text-center md:text-left mb-6 md:mb-11">
        {sectionName}
      </Typography>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-7">
        {items.map((item) => item._type === "category" ? (
          <div
            key={item._id}
            className="border border-bordered bg-paper rounded-[30px] overflow-hidden transition-shadow px-[26px]"
          >
            <a
              href={`/help-center/category/${item.slug}`}
              className="h-[100px] flex items-center cursor-pointer border-b border-bordered no-underline py-6"
              onClick={(e) => { e.preventDefault(); handleCategoryClick(item.slug) }}
              aria-label={`${texts?.view_category_aria_prefix || "View"} ${item.label} ${texts?.view_category_aria_suffix || "category"}`}
            >
              {item.icon && (
                <div className="mr-[10px]">
                  <img src={url(item.icon)} alt={`${item.label} icon`} className="w-[50px] h-[50px] object-cover" />
                </div>
              )}
              <Typography variant="h4" className="text-text hover:text-text">
                {item.label}
              </Typography>
              <div className="w-[30px] h-[30px] flex items-center justify-center bg-primary_lt rounded-full ml-auto">
                <ChevronRightIcon className="w-[18px] h-[18px] text-text stroke-2" />
              </div>
            </a>

            <div className="pt-4 pb-6 space-y-5">
              {item.children?.slice(0, 3).map((child) => child._itemType === "category" ? (
                <a
                  key={child._id}
                  href={`/help-center/category/${item.slug}/${child.slug}`}
                  className="flex items-start gap-4 rounded-[20px] cursor-pointer no-underline"
                  onClick={(e) => { e.preventDefault(); handleCategoryClick(child.slug) }}
                  aria-label={`${texts?.view_category_aria_prefix || "View"} ${child.label} ${texts?.view_category_aria_suffix || "category"}`}
                >
                  <Typography variant="body1" className="flex-grow hover:text-primary my-0">
                    {child.label}
                  </Typography>
                  <ChevronRightIcon className="w-5 h-5 text-text_secondary stroke-2 flex-shrink-0 mr-1 mt-0.5" />
                </a>
              ) : (
                <a
                  key={child._id}
                  href={`/help-center/category/${item.slug}/article/${child.slug}`}
                  className="flex items-start gap-4 rounded-[20px] cursor-pointer no-underline"
                  onClick={(e) => handleArticleClick(e, item.slug, child.slug)}
                  aria-label={`${texts?.read_article_aria_prefix || "Read"} ${child.label} ${texts?.read_article_aria_suffix || "article"}`}
                >
                  <Typography variant="body1" className="flex-grow hover:text-primary leading-tight transition-colors my-0">
                    {child.label}
                  </Typography>
                  <ChevronRightIcon className="w-5 h-5 text-text_secondary stroke-2 flex-shrink-0 mr-1 mt-0.5" />
                </a>
              ))}

              {!item.children?.length && (
                <div className="px-6 py-4">
                  <Typography variant="body1_dim">
                    {texts?.no_articles_in_cat || "No articles available"}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            key={item._id}
            className="h-[186px] flex flex-col items-center justify-center bg-paper border border-bordered hover:border-secondary rounded-[30px] p-8 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => handleDirectArticleClick(item.slug)}
          >
            <Typography variant="h4" className="text-center my-0">
              {item.label}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  )
}

if (typeof window !== "undefined") {
  window.initHelpCenterNavigation = (loadContentFunction) => {
    window.loadHelpCenterContent = loadContentFunction
  }
}

export default FeaturedCategories
